import React from 'react';
import { motion } from 'framer-motion';
import type { GameState } from '../../types/bountifulSea';

// ═══════════════════════════════════════════════════════════════
// ROUND SUMMARY COMPONENT
// ═══════════════════════════════════════════════════════════════

interface RoundSummaryProps {
  state: GameState;
  onContinue: () => void;
}

export const RoundSummary: React.FC<RoundSummaryProps> = ({
  state,
  onContinue,
}) => {
  const latestSummary = state.roundHistory[state.roundHistory.length - 1];
  const players = Object.values(state.players);

  // Sort players by haul for this round
  const sortedPlayers = [...players].sort((a, b) => {
    const haulA = latestSummary?.hauls[a.id] ?? 0;
    const haulB = latestSummary?.hauls[b.id] ?? 0;
    return haulB - haulA;
  });

  const loserIds = latestSummary?.loserPlayerIds ?? [];

  return (
    <div className="bs-round-summary bs-content-box">
      <motion.h2
        className="bs-summary-title"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        Round {latestSummary?.roundNumber ?? state.round.roundNumber} Complete
      </motion.h2>

      <motion.div
        className="bs-summary-hauls"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <h3 style={{ color: 'var(--bs-text-secondary)', marginBottom: '1rem' }}>
          Hauls This Round
        </h3>

        {sortedPlayers.map((player, index) => {
          const haul = latestSummary?.hauls[player.id] ?? 0;
          const isLoser = loserIds.includes(player.id);

          return (
            <motion.div
              key={player.id}
              className={`bs-summary-row ${isLoser ? 'bs-summary-row-loser' : ''}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <div className="bs-summary-player">
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: player.color,
                  }}
                />
                <span>{player.name}</span>
                {player.isEliminated && (
                  <span style={{ color: 'var(--bs-danger)', fontSize: '0.8rem' }}>
                    (Shipwrecked!)
                  </span>
                )}
              </div>
              <div className="bs-summary-haul">
                {haul} coins
                {isLoser && !player.isEliminated && (
                  <span style={{ color: 'var(--bs-danger)', marginLeft: '0.5rem' }}>
                    -1 🎲
                  </span>
                )}
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      <motion.p
        className="bs-summary-message"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        {state.message}
      </motion.p>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        style={{ textAlign: 'center' }}
      >
        <p style={{ color: 'var(--bs-text-muted)', marginBottom: '1rem' }}>
          Bountiful Sea remaining: <strong style={{ color: 'var(--bs-sea-teal)' }}>
            {state.bountifulSea}
          </strong> coins
        </p>

        <motion.button
          className="bs-btn bs-btn-primary bs-btn-large"
          onClick={onContinue}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {state.bountifulSea <= 0 ? 'See Final Results' : 'Next Round'}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default RoundSummary;
